"use client";
// Disable Global SSR

/* eslint-disable import/no-named-as-default -- that's posthog */
/* eslint-disable no-console -- debug */
import localFont from "next/font/local";
import Head from "next/head";
import { SnackbarProvider } from "notistack";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { IntercomProvider } from "react-use-intercom";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import { MediaQueryProvider } from "shared/providers";
import { AuthProvider } from "../common/providers/auth";
import "stream-chat-react/dist/css/v2/index.css";
import "../styles/globals.css";
import { config } from "../common/config";
import { ApplicationProvider } from "../common/providers/application";
import { OverlayProvider } from "../common/providers/loading-overlay";
// configs
import "../intl/config";
import { NewMessage } from "../common/providers/layout/components/new-message-snack";
import DatadogInit from "../common/providers/datadog";
import { ErrorBoundary } from "../common/providers/error-boundary";
import dynamic from "next/dynamic";

const ChatProvider = dynamic(() =>
  import("../common/providers/chat").then((mod) => mod.ChatProvider)
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

const font = localFont({
  src: [
    {
      path: "../common/fonts/Satoshi-Regular.otf",

      weight: "400"
    },
    {
      path: "../common/fonts/Satoshi-Medium.otf",
      weight: "500"
    },
    {
      path: "../common/fonts/Satoshi-Bold.otf",
      weight: "700"
    }
  ]
});

const INTERCOM_APP_ID = config.intercomId;

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== "undefined")
  // Key is public, so it's safe to expose it here
  posthog.init("phc_JXWufy2FAzFeDVGiDivnITZCpfJ4AfR2D8UmH1FZeOX", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    session_recording: {
      // WARNING: Only enable this if you understand the security implications
      recordCrossOriginIframes: true
    },
    // Enable debug mode in development
    loaded: (ph) => {
      if (config.environment === "staging") ph.debug();
    }
  });

const App = ({ Component, pageProps }) => {
  // DEBUG MODE
  useEffect(() => {
    if (config.environment !== "production")
      console.log("PATHNAME:// ", window.location.pathname);
  });

  const [unreadIntercomCount, setUnreadIntercomCount] = useState(0);

  return (
    <ErrorBoundary>
      <Head>
        <title>Rental application</title>
      </Head>
      <div id="custom-event-interceptor" />
      <DatadogInit />
      <MediaQueryProvider>
        <PostHogProvider apiKey={config.postHogKey || ""}>
          <OverlayProvider>
            <QueryClientProvider client={queryClient}>
              <main style={font.style}>
                <IntercomProvider
                  onUnreadCountChange={(c) => {
                    setUnreadIntercomCount(c);
                  }}
                  appId={String(INTERCOM_APP_ID)}
                  autoBoot
                  autoBootProps={{
                    customAttributes: {
                      source: "boomscreen_applicant"
                    },
                    hideDefaultLauncher: true
                  }}
                >
                  <AuthProvider unreadIntercomCount={unreadIntercomCount}>
                    <ApplicationProvider>
                      <ChatProvider>
                        <Component {...pageProps} />
                      </ChatProvider>
                    </ApplicationProvider>
                  </AuthProvider>
                </IntercomProvider>
                <SnackbarProvider
                  Components={{
                    // @ts-expect-error -- not sure what to do here
                    newMessage: NewMessage
                  }}
                />
              </main>
            </QueryClientProvider>
          </OverlayProvider>
        </PostHogProvider>
      </MediaQueryProvider>
    </ErrorBoundary>
  );
};

export default App;
